import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { allowedScreens } from '@functions/allowed-routes';
import { GoalMonth, ProgressLevel, goalMonthOf, goalProgress, hasGoal, progressLevel, rankGoals, shiftMonth } from '../../CRM/sales-goals/sales-goals';
import { SalesGoalsService } from '../../CRM/sales-goals/services/sales-goals.service';
import { SalesGoalsMonthResponse, SalesGoalRow } from '../../CRM/sales-goals/services/responses/sales-goals.response';
import { Panel, PanelState, money, percent, useDashboardColors } from './panel';

/// A tela do backend que libera a edição das metas.
const SALES_GOALS_SCREEN = 'SalesGoals';

const PAGE_SIZE = 5;

interface Props {
    /// Último dia do período do painel: o mês dele é o que abre.
    periodEnd: string;
    /// Muda quando a pessoa pede para atualizar o painel.
    refreshKey: number;
}

interface GoalBarProps {
    label: string;
    achieved: string;
    target: string;
    progress: number;
}

/// Uma meta de vendedor: barra até a meta e o percentual em texto, para quem não distingue cores.
const GoalBar = ({ label, achieved, target, progress }: GoalBarProps) => {
    const { t } = useTranslation();
    const cc = useDashboardColors();

    const colors: Record<ProgressLevel, string> = {
        done: cc.eventSuccess,
        onTrack: cc.accent,
        behind: cc.eventWarning,
    };

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr auto', sm: '90px 1fr auto' },
                alignItems: 'center',
                columnGap: 1.5,
                rowGap: 0.4,
            }}
        >
            <Typography sx={{ color: cc.textSecondary, fontSize: '0.74rem' }}>{label}</Typography>

            <Box
                sx={{
                    gridColumn: { xs: '1 / -1', sm: 'auto' },
                    gridRow: { xs: 2, sm: 'auto' },
                    height: 10,
                    borderRadius: '4px',
                    background: cc.countBg,
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        width: `${Math.min(progress, 100)}%`,
                        minWidth: progress > 0 ? 4 : 0,
                        height: '100%',
                        borderRadius: '4px',
                        background: colors[progressLevel(progress)],
                        transition: 'width .25s',
                    }}
                />
            </Box>

            <Typography
                sx={{
                    color: cc.textPrimary,
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    fontVariantNumeric: 'tabular-nums',
                    textAlign: 'right',
                }}
            >
                {t('metas.progresso', { feito: achieved, meta: target })}
                <Box component="span" sx={{ color: colors[progressLevel(progress)], fontSize: '0.74rem' }}>
                    {' · '}{percent(progress)}
                </Box>
            </Typography>
        </Box>
    );
};

/// Meta do mês de cada vendedor contra o ganho; segue só a visibilidade, não os filtros do painel.
export const SalesGoalsPanel = ({ periodEnd, refreshKey }: Props) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const cc = useDashboardColors();
    const { showAlert } = useAlert();

    const [month, setMonth] = useState<GoalMonth>(() => goalMonthOf(periodEnd));
    const [data, setData] = useState<SalesGoalsMonthResponse | undefined>();
    const [page, setPage] = useState(0);
    const canEdit = useMemo(() => allowedScreens().has(SALES_GOALS_SCREEN), []);
    const ranked = useMemo(() => rankGoals((data?.users ?? []).filter(hasGoal)), [data]);
    const pageCount = Math.ceil(ranked.length / PAGE_SIZE);

    // Trocar de mês rápido dispara pedidos em sequência; o velho não pode sobrescrever o novo.
    const requestRef = useRef(0);

    // Mesmo mês é o mesmo objeto: trocar só o dia do período não recarrega.
    useEffect(() => {
        const next = goalMonthOf(periodEnd);

        setMonth((old) => (old.year === next.year && old.month === next.month ? old : next));
    }, [periodEnd]);

    const load = useCallback(async () => {
        const request = ++requestRef.current;

        setData(undefined);
        setPage(0);

        try {
            const reply = await new SalesGoalsService().goals(month.year, month.month);

            if (request === requestRef.current) setData(reply.data.data);
        } catch (err) {
            if (request !== requestRef.current) return;

            showAlert(apiErrorMessage(err), 'error', 3000);
            setData({ ...month, users: [] });
        }
    }, [month]);

    useEffect(() => {
        load();
    }, [load, refreshKey]);

    const monthLabel = new Date(month.year, month.month - 1, 1)
        .toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });

    const count = (value: number) => t('metas.vendasQtd', { count: value });

    const row = (user: SalesGoalRow) => {
        // Meta em branco (ou zero) não ganha barra: só aparece o que foi cadastrado.
        const revenueProgress = goalProgress(user.achievedRevenue, user.revenueTarget);
        const salesProgress = goalProgress(user.achievedSales, user.salesTarget);

        return (
            <Box key={user.userId} sx={{ display: 'grid', gap: 0.5, py: 0.75, px: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, background: user.color }} />
                    <Typography
                        sx={{
                            color: cc.textPrimary,
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {user.name}
                    </Typography>
                </Box>

                {revenueProgress !== null && (
                    <GoalBar
                        label={t('metas.faturamento')}
                        achieved={money(user.achievedRevenue)}
                        target={money(user.revenueTarget)}
                        progress={revenueProgress}
                    />
                )}
                {salesProgress !== null && (
                    <GoalBar
                        label={t('metas.vendas')}
                        achieved={count(user.achievedSales)}
                        target={count(user.salesTarget)}
                        progress={salesProgress}
                    />
                )}
            </Box>
        );
    };

    return (
        <Panel
            title={t('metas.titulo')}
            subtitle={t('metas.ajuda')}
            action={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Tooltip title={t('metas.mesAnterior')}>
                        <IconButton
                            size="small"
                            aria-label={t('metas.mesAnterior')}
                            onClick={() => setMonth((old) => shiftMonth(old, -1))}
                            sx={{ color: cc.textSecondary }}
                        >
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Typography
                        sx={{
                            color: cc.textPrimary,
                            fontSize: '0.82rem',
                            fontWeight: 600,
                            '&::first-letter': { textTransform: 'uppercase' },
                            minWidth: 120,
                            textAlign: 'center',
                        }}
                    >
                        {monthLabel}
                    </Typography>
                    <Tooltip title={t('metas.proximoMes')}>
                        <IconButton
                            size="small"
                            aria-label={t('metas.proximoMes')}
                            onClick={() => setMonth((old) => shiftMonth(old, 1))}
                            sx={{ color: cc.textSecondary }}
                        >
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {canEdit && (
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={!data?.users.length}
                            onClick={() => navigate(`/crm/metas?year=${month.year}&month=${month.month}`)}
                            sx={{ textTransform: 'none', color: cc.accent, borderColor: cc.border, flexShrink: 0, ml: 1 }}
                        >
                            {t('metas.definir')}
                        </Button>
                    )}
                </Box>
            }
        >
            {!data || ranked.length === 0 ? (
                <PanelState
                    loading={!data}
                    empty={data?.users.length ? t('metas.nenhumaMeta') : t('metas.semVendedores')}
                />
            ) : (
                <Box sx={{ display: 'grid', gap: 0.25 }}>
                    {ranked.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map(row)}

                    {pageCount > 1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, pt: 0.5 }}>
                            <IconButton
                                size="small"
                                aria-label={t('metas.paginaAnterior')}
                                disabled={page === 0}
                                onClick={() => setPage((old) => old - 1)}
                                sx={{ color: cc.textSecondary }}
                            >
                                <ChevronLeftIcon fontSize="small" />
                            </IconButton>
                            <Typography sx={{ color: cc.textSecondary, fontSize: '0.74rem', fontVariantNumeric: 'tabular-nums' }}>
                                {page + 1} / {pageCount}
                            </Typography>
                            <IconButton
                                size="small"
                                aria-label={t('metas.proximaPagina')}
                                disabled={page >= pageCount - 1}
                                onClick={() => setPage((old) => old + 1)}
                                sx={{ color: cc.textSecondary }}
                            >
                                <ChevronRightIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            )}

        </Panel>
    );
};
